import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Key as KeyIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  ContentCopy as CopyIcon,
  ExpandMore as ExpandMoreIcon,
  Security as SecurityIcon,
  Computer as ComputerIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import apiService from '../services/api';

const SSHKeyManagePage = () => {
  const [hostKey, setHostKey] = useState(null);
  const [vmKeys, setVmKeys] = useState({});
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [showRegenerateDialog, setShowRegenerateDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [selectedKey, setSelectedKey] = useState(null);
  const [showKeyDialog, setShowKeyDialog] = useState(false);
  const [showGenerateVMKeyDialog, setShowGenerateVMKeyDialog] = useState(false);
  const [allVms, setAllVms] = useState([]);
  const [selectedVmForGenerate, setSelectedVmForGenerate] = useState('');

  useEffect(() => {
    loadKeys();
  }, []);

  const loadKeys = async () => {
    try {
      setLoading(true);

      // 호스트 키 조회
      const hostKeyResponse = await apiService.getHostSSHKey();
      setHostKey(hostKeyResponse.data);

      // VM 키 목록 조회
      const vmKeysResponse = await apiService.getVMsSSHKeys();
      setVmKeys(vmKeysResponse.data.vm_keys || {});

      // 전체 VM 목록 조회 (all=true로 모든 VM 포함)
      const vmsResponse = await apiService.getVMs(true);
      setAllVms(vmsResponse.data?.vms || []);

    } catch (error) {
      showMessage('SSH 키 정보 로드 실패: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerateHostKey = async () => {
    try {
      setRegenerating(true);
      const response = await apiService.regenerateHostSSHKey();

      if (response.status === 200) {
        // 개인키가 반환되면 자동으로 다운로드
        if (response.data && response.data.private_key) {
          downloadPrivateKey('host', response.data.private_key);
          showMessage('호스트 SSH 키가 성공적으로 재생성되었습니다. 개인키가 다운로드됩니다.', 'success');
        } else {
          showMessage('호스트 SSH 키가 성공적으로 재생성되었습니다.', 'success');
        }
        await loadKeys();
      }
    } catch (error) {
      showMessage('SSH 키 재생성 실패: ' + error.message, 'error');
    } finally {
      setRegenerating(false);
      setShowRegenerateDialog(false);
    }
  };

  const handleGenerateVMKey = async () => {
    if (!selectedVmForGenerate) {
      showMessage('VM을 선택해주세요.', 'warning');
      return;
    }

    try {
      const response = await apiService.generateVMSSHKey(selectedVmForGenerate);

      // 개인키가 반환되면 자동으로 다운로드
      if (response.data && response.data.private_key) {
        downloadPrivateKey(selectedVmForGenerate, response.data.private_key);

        // 설치 상태에 따라 다른 메시지 표시
        let message = `VM "${selectedVmForGenerate}" SSH 키가 생성되었습니다. 개인키가 다운로드됩니다.`;
        let severity = 'success';

        if (response.data.install_status) {
          if (response.data.install_status.installed) {
            message += ` 공개키가 VM에 자동으로 설치되었습니다.`;
          } else {
            message += ` 주의: ${response.data.install_status.message}`;
            severity = 'warning';
          }
        }

        showMessage(message, severity);
      } else {
        showMessage(`VM "${selectedVmForGenerate}" SSH 키가 생성되었습니다.`, 'success');
      }

      setShowGenerateVMKeyDialog(false);
      setSelectedVmForGenerate('');
      await loadKeys();
    } catch (error) {
      showMessage('VM SSH 키 생성 실패: ' + error.message, 'error');
    }
  };

  const downloadPrivateKey = (vmName, privateKeyContent) => {
    // Blob 생성
    const blob = new Blob([privateKeyContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);

    // 다운로드 링크 생성 및 클릭
    const link = document.createElement('a');
    link.href = url;
    link.download = `${vmName}_private_key.pem`;
    document.body.appendChild(link);
    link.click();

    // 정리
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const getAvailableVMsForKeyGeneration = () => {
    // 키가 없는 VM만 반환
    if (!Array.isArray(allVms)) {
      return [];
    }
    return allVms.filter(vm => !vmKeys[vm.name]);
  };

  const handleDeleteVMKey = async (vmName) => {
    try {
      await apiService.deleteVMSSHKey(vmName);
      showMessage(`VM "${vmName}" SSH 키가 삭제되었습니다.`, 'success');
      await loadKeys();
    } catch (error) {
      showMessage('VM SSH 키 삭제 실패: ' + error.message, 'error');
    }
  };

  const copyToClipboard = async (text) => {
    try {
      // 최신 브라우저 API 사용
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        showMessage('클립보드에 복사되었습니다.', 'success');
      } else {
        // 폴백: 전통적인 방법 사용
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        try {
          const successful = document.execCommand('copy');
          if (successful) {
            showMessage('클립보드에 복사되었습니다.', 'success');
          } else {
            throw new Error('복사 실패');
          }
        } finally {
          document.body.removeChild(textArea);
        }
      }
    } catch (error) {
      showMessage('클립보드 복사에 실패했습니다. 수동으로 복사해주세요.', 'error');
    }
  };

  const showMessage = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const getKeyType = (publicKey) => {
    if (publicKey.startsWith('ssh-ed25519')) return 'Ed25519';
    if (publicKey.startsWith('ssh-rsa')) return 'RSA';
    if (publicKey.startsWith('ecdsa-sha2')) return 'ECDSA';
    return 'Unknown';
  };

  const getKeyTypeColor = (keyType) => {
    switch (keyType) {
      case 'Ed25519': return 'success';
      case 'RSA': return 'primary';
      case 'ECDSA': return 'secondary';
      default: return 'default';
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    return new Date(parseFloat(timestamp) * 1000).toLocaleString('ko-KR');
  };

  const truncateKey = (key, length = 50) => {
    if (key.length <= length) return key;
    return key.substring(0, length) + '...';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <KeyIcon sx={{ fontSize: 32, mr: 2, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            SSH 키 관리
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadKeys}
          disabled={loading}
        >
          새로고침
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* 호스트 SSH 키 섹션 */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SecurityIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">호스트 SSH 키</Typography>
                <Chip
                  size="small"
                  label="터미널 접속용"
                  color="primary"
                  sx={{ ml: 2 }}
                />
              </Box>

              {hostKey?.status === 'exists' ? (
                <Box>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={8}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          공개키 (클릭하여 복사)
                        </Typography>
                        <Paper
                          sx={{ 
                            p: 2, 
                            bgcolor: 'grey.50', 
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'grey.100' }
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            copyToClipboard(hostKey.public_key);
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontFamily: 'monospace', 
                                wordBreak: 'break-all',
                                flex: 1
                              }}
                            >
                              {truncateKey(hostKey.public_key, 80)}
                            </Typography>
                            <IconButton 
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                copyToClipboard(hostKey.public_key);
                              }}
                              sx={{ ml: 1 }}
                            >
                              <CopyIcon />
                            </IconButton>
                          </Box>
                        </Paper>
                      </Box>

                      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <Chip
                          icon={<CheckCircleIcon />}
                          label={`키 타입: ${getKeyType(hostKey.public_key)}`}
                          color={getKeyTypeColor(getKeyType(hostKey.public_key))}
                          size="small"
                        />
                        <Chip
                          icon={<InfoIcon />}
                          label={`생성일: ${formatDate(hostKey.created_at)}`}
                          variant="outlined"
                          size="small"
                        />
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Button
                          variant="outlined"
                          color="warning"
                          startIcon={<RefreshIcon />}
                          onClick={() => setShowRegenerateDialog(true)}
                          disabled={regenerating}
                          fullWidth
                        >
                          키 재생성
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={<CopyIcon />}
                          onClick={() => {
                            setSelectedKey(hostKey);
                            setShowKeyDialog(true);
                          }}
                          fullWidth
                        >
                          전체 키 보기
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <ErrorIcon sx={{ fontSize: 48, color: 'error.main', mb: 2 }} />
                  <Typography variant="h6" color="error" gutterBottom>
                    호스트 SSH 키가 없습니다
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    터미널 접속을 위해 호스트 SSH 키가 필요합니다.
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleRegenerateHostKey}
                    disabled={regenerating}
                  >
                    호스트 키 생성
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* VM SSH 키 섹션 */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ComputerIcon sx={{ mr: 1, color: 'secondary.main' }} />
                  <Typography variant="h6">VM SSH 키</Typography>
                  <Chip
                    size="small"
                    label={`${Object.keys(vmKeys).length}개`}
                    color="secondary"
                    sx={{ ml: 2 }}
                  />
                </Box>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<AddIcon />}
                  onClick={() => setShowGenerateVMKeyDialog(true)}
                  disabled={getAvailableVMsForKeyGeneration().length === 0}
                >
                  VM 키 생성
                </Button>
              </Box>

              {/* VM SSH 키 설명 */}
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  <strong>VM SSH 키란?</strong>
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  VM별로 개별적으로 생성하는 SSH 인증 키페어입니다. 호스트 키와 별도로 관리되며,
                  특정 VM에만 접근 권한을 제한하거나 외부 사용자에게 특정 VM 접근 권한을 부여할 때 유용합니다.
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Typography variant="body2" component="div">
                  <strong>사용 방법:</strong>
                  <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 2 }}>
                    <li>키 생성 시 개인키(.pem 파일)가 자동으로 다운로드됩니다</li>
                    <li>개인키 파일의 권한을 설정합니다: <code>chmod 600 파일명.pem</code></li>
                    <li>SSH 접속: <code>ssh -i 파일명.pem 사용자@VM주소</code></li>
                    <li>⚠️ 개인키는 생성 시 한 번만 다운로드되므로 안전하게 보관하세요</li>
                  </Box>
                </Typography>
              </Alert>

              {Object.keys(vmKeys).length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>VM 이름</TableCell>
                        <TableCell>키 타입</TableCell>
                        <TableCell>생성일</TableCell>
                        <TableCell>공개키 (미리보기)</TableCell>
                        <TableCell align="center">작업</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(vmKeys).map(([vmName, keyInfo]) => (
                        <TableRow key={vmName}>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {vmName}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={getKeyType(keyInfo.public_key)}
                              color={getKeyTypeColor(getKeyType(keyInfo.public_key))}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {formatDate(keyInfo.created_at)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box
                              sx={{ 
                                fontFamily: 'monospace', 
                                fontSize: '0.75rem',
                                cursor: 'pointer',
                                '&:hover': { bgcolor: 'grey.100' },
                                p: 0.5,
                                borderRadius: 1
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                copyToClipboard(keyInfo.public_key);
                              }}
                            >
                              {truncateKey(keyInfo.public_key, 40)}
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                              <Tooltip title="전체 키 보기">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setSelectedKey({ ...keyInfo, vm_name: vmName });
                                    setShowKeyDialog(true);
                                  }}
                                >
                                  <InfoIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="복사">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    copyToClipboard(keyInfo.public_key);
                                  }}
                                >
                                  <CopyIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="삭제">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteVMKey(vmName)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <ComputerIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    VM SSH 키가 없습니다
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    VM별 개별 SSH 키는 필요 시 생성됩니다.
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* 도움말 섹션 */}
        <Grid item xs={12}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">SSH 키 관리 도움말</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                {/* 호스트 SSH 키 */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ p: 2, bgcolor: 'primary.50', borderRadius: 1 }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', color: 'primary.main' }}>
                      <SecurityIcon sx={{ mr: 1 }} />
                      <strong>호스트 SSH 키</strong>
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <List dense>
                      <ListItem>
                        <ListItemText
                          primary="📌 마스터 SSH 키"
                          secondary="웹 터미널 접속에 사용되는 시스템 전체의 기본 SSH 키입니다"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="🔄 자동 배포"
                          secondary="VM 생성 시 cloud-init을 통해 자동으로 authorized_keys에 추가됩니다"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="🔐 Ed25519 권장"
                          secondary="최신 암호화 방식으로 RSA보다 더 안전하고 빠릅니다"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="⚙️ 재생성 가능"
                          secondary="문제 발생 시 재생성하면 모든 활성 터널이 종료되고 새 키가 배포됩니다"
                        />
                      </ListItem>
                    </List>
                  </Box>
                </Grid>

                {/* VM SSH 키 */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ p: 2, bgcolor: 'secondary.50', borderRadius: 1 }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', color: 'secondary.main' }}>
                      <ComputerIcon sx={{ mr: 1 }} />
                      <strong>VM SSH 키</strong>
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <List dense>
                      <ListItem>
                        <ListItemText
                          primary="🎯 개별 VM 전용 키"
                          secondary="특정 VM에만 접근 권한을 제한할 때 사용하는 독립적인 SSH 키입니다"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="💾 개인키 자동 다운로드"
                          secondary="키 생성 즉시 개인키(.pem)가 자동으로 다운로드되며, 이후 조회는 불가능합니다"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="⚠️ 보안 주의사항"
                          secondary="개인키는 생성 시 단 한 번만 제공됩니다. 분실 시 키를 삭제 후 재생성해야 합니다"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="🔧 독립적 관리"
                          secondary="VM별로 개별적으로 생성/삭제 가능하며 호스트 키와 별도로 관리됩니다"
                        />
                      </ListItem>
                    </List>
                  </Box>
                </Grid>

                {/* 사용 시나리오 */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" gutterBottom>
                    <strong>💡 언제 VM SSH 키를 사용하나요?</strong>
                  </Typography>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} md={4}>
                      <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                        <Typography variant="subtitle2" gutterBottom color="primary">
                          외부 사용자 접근 제한
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          특정 VM에만 접근 권한을 부여하고 싶을 때, VM 전용 SSH 키를 외부 사용자에게 제공할 수 있습니다.
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                        <Typography variant="subtitle2" gutterBottom color="primary">
                          보안 강화
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          중요한 VM의 경우 호스트 키와 별도의 추가 인증 키를 사용하여 이중 보안을 적용할 수 있습니다.
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                        <Typography variant="subtitle2" gutterBottom color="primary">
                          키 교체 테스트
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          호스트 키를 교체하기 전에 VM 키로 테스트하여 안전하게 SSH 키 관리 프로세스를 검증할 수 있습니다.
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>
      </Grid>

      {/* 키 재생성 확인 다이얼로그 */}
      <Dialog
        open={showRegenerateDialog}
        onClose={() => setShowRegenerateDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <RefreshIcon sx={{ mr: 1, color: 'warning.main' }} />
            호스트 SSH 키 재생성
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            기존 SSH 키가 삭제되고 새로운 키가 생성됩니다.
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            • 현재 활성화된 모든 터미널 연결이 끊어집니다<br/>
            • VM에 새 키가 자동으로 적용됩니다<br/>
            • <strong>새 개인키가 자동으로 다운로드됩니다</strong><br/>
            • 이 작업은 되돌릴 수 없습니다
          </Typography>
          <Alert severity="info">
            <Typography variant="body2">
              💡 개인키는 재생성 시 한 번만 다운로드되며, 이후 조회가 불가능합니다.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRegenerateDialog(false)}>
            취소
          </Button>
          <Button
            onClick={handleRegenerateHostKey}
            variant="contained"
            color="warning"
            disabled={regenerating}
            startIcon={regenerating ? <CircularProgress size={20} /> : <RefreshIcon />}
          >
            {regenerating ? '재생성 중...' : '재생성'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* VM SSH 키 생성 다이얼로그 */}
      <Dialog
        open={showGenerateVMKeyDialog}
        onClose={() => {
          setShowGenerateVMKeyDialog(false);
          setSelectedVmForGenerate('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AddIcon sx={{ mr: 1, color: 'secondary.main' }} />
            VM SSH 키 생성
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2, mt: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              <strong>중요: 개인키 보안</strong>
            </Typography>
            <Typography variant="body2">
              • 개인키는 생성 시 <strong>한 번만</strong> 다운로드됩니다<br/>
              • 분실 시 재생성이 필요하며 기존 키는 삭제됩니다<br/>
              • 개인키는 안전한 곳에 보관하세요
            </Typography>
          </Alert>

          {getAvailableVMsForKeyGeneration().length > 0 ? (
            <FormControl fullWidth>
              <InputLabel id="vm-select-label">VM 선택</InputLabel>
              <Select
                labelId="vm-select-label"
                value={selectedVmForGenerate}
                label="VM 선택"
                onChange={(e) => setSelectedVmForGenerate(e.target.value)}
              >
                {getAvailableVMsForKeyGeneration().map((vm) => (
                  <MenuItem key={vm.name} value={vm.name}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ComputerIcon fontSize="small" />
                      <Typography>{vm.name}</Typography>
                      <Chip
                        label={vm.state}
                        size="small"
                        color={vm.state === 'running' ? 'success' : 'default'}
                        sx={{ ml: 1 }}
                      />
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <Alert severity="warning">
              모든 VM에 이미 SSH 키가 생성되어 있습니다.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowGenerateVMKeyDialog(false);
            setSelectedVmForGenerate('');
          }}>
            취소
          </Button>
          <Button
            onClick={handleGenerateVMKey}
            variant="contained"
            color="secondary"
            disabled={!selectedVmForGenerate}
            startIcon={<AddIcon />}
          >
            생성
          </Button>
        </DialogActions>
      </Dialog>

      {/* 전체 키 보기 다이얼로그 */}
      <Dialog
        open={showKeyDialog}
        onClose={() => setShowKeyDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <KeyIcon sx={{ mr: 1 }} />
            SSH 키 상세 정보
            {selectedKey?.vm_name && (
              <Chip label={selectedKey.vm_name} size="small" sx={{ ml: 2 }} />
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedKey && (
            <Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  키 타입
                </Typography>
                <Chip
                  label={getKeyType(selectedKey.public_key)}
                  color={getKeyTypeColor(getKeyType(selectedKey.public_key))}
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  생성일
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatDate(selectedKey.created_at)}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  공개키
                </Typography>
                <Paper
                  sx={{ 
                    p: 2, 
                    bgcolor: 'grey.50',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'grey.100' },
                    position: 'relative'
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    copyToClipboard(selectedKey.public_key);
                  }}
                >
                  <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                    <IconButton 
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        copyToClipboard(selectedKey.public_key);
                      }}
                      sx={{ bgcolor: 'white', '&:hover': { bgcolor: 'grey.100' } }}
                    >
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontFamily: 'monospace', 
                      wordBreak: 'break-all',
                      fontSize: '0.875rem'
                    }}
                  >
                    {selectedKey.public_key}
                  </Typography>
                </Paper>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<CopyIcon />}
            onClick={(e) => {
              e.stopPropagation();
              selectedKey && copyToClipboard(selectedKey.public_key);
            }}
          >
            복사
          </Button>
          <Button onClick={() => setShowKeyDialog(false)}>
            닫기
          </Button>
        </DialogActions>
      </Dialog>

      {/* 스낵바 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SSHKeyManagePage; 